import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';

const toolName = 'apply-structured-brief' as const;

/**
 * The editable content fields of `@packages/domains/campaigns/data-types`
 * `StructuredBrief` (metadata fields are application-managed and excluded).
 *
 * Kept as a self-contained copy rather than importing the campaigns
 * `BRIEF_CONTENT_FIELD_KEYS`: this manifest is re-exported from the shared
 * `tool-registry` barrel, and the campaigns data-types barrel transitively pulls
 * `site-access` → `code-libraries` → `wfdl` → `design-system`, tripping a
 * load-order circular import in every barrel consumer. A shared infra package
 * must not depend on a domain.
 *
 * Drift from the schema is no longer one-directional: the apply engine
 * (`applyBriefEditOps`) and the MCP `edit-campaign-brief` DTO both validate
 * against the schema-derived `CONTENT_FIELD_KEYS`, which as of CAM-1454
 * deliberately excludes `tone`/`painPoints`/`proofPoints` (removed from the
 * brief form, so the agent must not edit what the user can't see). If this
 * list is left wider than `CONTENT_FIELD_KEYS`, the LLM is told it may edit a
 * field the runtime then rejects — a failed turn for the user, not just a
 * narrower-than-necessary tool schema. The two lists must be narrowed
 * together. A cross-package drift guard is a follow-up; it must avoid
 * importing the campaigns data-types barrel (which re-triggers the circular
 * import above). (CAM-853)
 */
export const BRIEF_FIELD_KEYS = [
  'campaignName',
  'summary',
  'campaignGoals',
  'targetAudience',
  'targetAudiences',
  'awarenessStage',
  'cta',
  'messagingFramework',
  'keyMessages',
  'differentiator',
  'channels',
  'campaignTarget',
  'timeline',
  'landingPageDescription',
  'brandName',
] as const;

/**
 * The brief-editor agent's single terminal tool. Rather than re-emitting the
 * whole brief (which risks the model silently drifting fields the user did not
 * ask to change), the tool takes a list of field-scoped edit operations. The
 * `designer` (external) tool has no server execute: the brief-editor client
 * stream manager pauses on the tool call, reads these ops, applies each to the
 * current brief, validates the result against the `StructuredBrief` schema, and
 * pushes it through the form's `onChange`. Unchanged fields are untouched by
 * construction.
 */
const briefEditOp = z.object({
  field: z
    .enum(BRIEF_FIELD_KEYS)
    .describe(
      'Which brief field this operation edits. targetAudiences covers personas, ICP, buyer profiles, target accounts, and similar phrases describing who the campaign targets — use it even if the user does not say "target audience" literally.'
    ),
  op: z.enum(['set', 'append', 'remove']).describe(
    dedent`
        The edit to perform:
        - "set": replace a text field's value, or replace a list field's entire array.
        - "append": add one item to a list field (campaignGoals, keyMessages, channels, targetAudiences).
        - "remove": delete one item from a list field (matched exactly).
        Only "set" is valid for text fields.
      `
  ),
  value: z.array(z.string()).describe(
    dedent`
        The operand — ALWAYS an array of strings:
        - "set" on a text field → a single-element array with the new string.
        - "set" on a list field → the full new array of strings.
        - "append"/"remove" on a list field → a single-element array with the item.
      `
  ),
});

const argumentZod = z.object({
  ops: z
    .array(briefEditOp)
    .default([])
    .describe(
      'The edits to apply. Include ONLY the fields the user asked to change — one operation per change. May be empty when you are only replying to the user (e.g. leaving a note or asking a question) and not editing any field this turn.'
    ),
  message: z
    .string()
    .optional()
    .describe(
      'A short, friendly note to the user — what you changed and WHY (especially if you had to adjust their request, e.g. shortened a name to fit the 24-character limit), plus an offer to help further or a suggestion for a next step.'
    ),
});

export const applyStructuredBriefTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Apply brief edits',
  prettyStatus: {
    executing: 'Updating brief…',
    executed: 'Brief updated',
    failed: 'Failed to update brief',
  },
  prettyErrors: {
    fallback: 'Unable to update the brief. Please try again.',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Call this tool when you are editing the campaign brief. Pass the edits as
      a list of field-scoped operations in \`ops\`, and include a \`message\`
      explaining what you changed and why. \`ops\` may be empty if you are only
      leaving a note or asking a question and not editing any field this turn.

      Emit ONE operation per field the user asked to change — do NOT re-emit
      unchanged fields. Fields you omit are left exactly as they are.

      The value field is always an array of strings.
      - Text field edit → {field, op: "set", value: ["<new text>"]}.
      - Add a list item (e.g. "add LinkedIn as a channel") →
        {field: "channels", op: "append", value: ["LinkedIn"]}.
      - Remove a list item → {field, op: "remove", value: ["<exact item>"]}.
      - Replace a whole list → {field, op: "set", value: ["a", "b"]}.

      The current brief is provided in the prompt as JSON — read it to compute
      exact list items for append/remove.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  confirmBeforeExecuting: false,
  timeout: 30000,
});
